/**
 * LaunchForm — the shell of the reworked mortgagee launch form.
 *
 * It is CONTROLLED by ONE flat values record (`values` / `setValues`, the
 * drawer's existing state) in which the action slice lives at
 * `values[binding.actionsKey]` and everything else is run level. There is no
 * `{ runLevelValues, actions }` envelope on the wire between this form and its
 * host: whoever needs the two slices derives them with `splitLaunchValues`. That
 * is what lets the drawer's `handleLaunch(next)` use the record it is handed and
 * its `handleRetry()` — which takes no argument and reads the drawer's own state
 * — reach the same builder and therefore the same shape.
 *
 * `LaunchForm` does NOT assemble the payload. Pressing Launch calls
 * `onSubmit(values)`; `buildLaunchPayload` is the one assembler, wired by the
 * test harness in Phases 3–4 and by the drawer in Phase 5.
 *
 * The shell is a `<div>`, never a `<form>`: `JsonSchemaForm` renders a `<form>`
 * element per action card, and nested forms are invalid HTML and break submit
 * semantics.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import CustomTooltip from "~/common/components/CustomTooltip";
import type { ConfidenceLevel } from "~/common/workflows/components/JsonSchemaForm";
import { ADDRESS_PART_KEYS } from "~/common/workflows/launch/addressParts";
import { computeLaunchBlocker } from "~/common/workflows/launch/core/launchBlocker";
import {
  isBlank,
  normaliseLaunchValues,
} from "~/common/workflows/launch/normaliseLaunchValues";
import PolicyActionGroup from "~/common/workflows/launch/PolicyActionGroup";
import { replyDraftValue } from "~/common/workflows/launch/buildLaunchPayload";
import ReplyDraftControl from "~/common/workflows/launch/ReplyDraftControl";
import ReanalyseControl, {
  type ReanalyseResult,
} from "~/common/workflows/launch/ReanalyseControl";
import {
  OpenEmailLink,
  type OpenEmailResult,
} from "~/common/workflows/components/OpenEmailLink";
import { OLD_INTEREST_NAME_KEY } from "~/common/workflows/launch/ActionCard";
import { resolveTermIdentityPlacement } from "~/common/workflows/launch/PolicyTermField";
import {
  splitLaunchValues,
  type LaunchBinding,
} from "~/common/workflows/launch/launchSchemaBinding";
import { readPolicyOptions } from "~/common/workflows/launch/policyPrefillOptions";
import {
  PRIMARY_ACTION_BUTTON_CLASS,
  TYPE_BODY,
  TYPE_LABEL,
} from "~/common/workflows/paneTypography";

// Re-exported so existing imports and tests keep working.
export { normaliseLaunchValues };

function titleOf(
  schema: Record<string, unknown> | null,
  key: string,
  fallback: string,
): string {
  const props = schema?.properties as Record<string, unknown> | undefined;
  const def = props?.[key] as Record<string, unknown> | undefined;
  return typeof def?.title === "string" ? def.title : fallback;
}

/**
 * Project a flat, FIELD-NAME-keyed record (`fieldConfidence` / `fieldLoading`)
 * onto one slice.
 *
 * Handing the flat record straight to `ActionCard` is not wrong-LOOKING — it
 * type-checks and renders — it just silently drops every prefill confidence chip
 * the AM uses to decide what to re-check.
 */
export function projectFieldMap<T>(
  source: Record<string, T> | undefined,
  keys: ReadonlySet<string>,
  extra: readonly string[] = [],
): Record<string, T> | undefined {
  if (!source) return undefined;
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(source)) {
    if (keys.has(key) || extra.includes(key)) out[key] = value;
  }
  return out;
}

export interface LaunchFormProps {
  binding: LaunchBinding;
  values: Record<string, unknown>;
  /**
   * The drawer's own `useState` setter. It accepts a FUNCTIONAL update because
   * RE-ANALYSE needs one: the host merges the fresh brief into `values` while
   * this form's `onReanalyse` promise is still settling, so a drop computed
   * from the closure's `values` would silently clobber the brief it just
   * fetched. Every other write here passes a plain record, as before.
   */
  setValues: Dispatch<SetStateAction<Record<string, unknown>>>;
  /** ONE values record — exactly the render-prop signature the drawer declares. */
  onSubmit: (next: Record<string, unknown>) => void;
  submitting?: boolean;
  /** A precondition OUTSIDE the payload (SP pool empty, client unpicked, …). */
  submitDisabledReason?: string | null;
  /**
   * True while a background prefill is in flight. The create-rule waits it out:
   * a first-paint create against an empty list freezes an empty action that the
   * drawer's later shallow merge cannot enter. Suites that mount a complete
   * fixture as initial `values` omit it and the create-rule runs on first paint.
   */
  prefillLoading?: boolean;
  fieldConfidence?: Record<string, ConfidenceLevel>;
  fieldLoading?: Record<string, boolean>;
  /**
   * Host-supplied re-analysis. RE-ANALYSE renders only when it is provided —
   * each host decides per-brief whether to offer it (e.g. ora's
   * `workflows.triage.$messageId` route passes it only when the loaded
   * brief's `can_reanalyze` flag is true), so the control is absent when a
   * host declines to wire it up for the current brief, rather than present
   * and broken.
   *
   * The host owns the whole server round trip (flip `prefillLoading` true,
   * `reanalyzeEmail`, refresh `lastExtraction`, `reanalysePrefill`, merge). This
   * form owns exactly one thing on top of it: dropping the action list on
   * SUCCESS so the create-rule rebuilds from the fresh brief.
   */
  onReanalyse?: () => Promise<ReanalyseResult | void>;
  /**
   * Host-supplied hop to the email this brief was prepared from, rendered as
   * Open Email to the right of RE-ANALYSE. A host passes it only when it can
   * actually open that email and the email is not already on screen — the
   * pane opened on an ora notification is the case it exists for. A
   * `{ ok: false }` result is shown under the link instead of failing silently.
   */
  onOpenEmail?: () => OpenEmailResult | void;
  /** Extra run-level content from the host (oversight), rendered in RUN OPTIONS. */
  runLevelControls?: ReactNode;
  /**
   * The drawer-owned Epic sign-in fallback, placed right ABOVE the submit
   * button (pinned with it). The host passes null when there is nothing to
   * say — story 28633: sign-in shows only when the default is unavailable.
   */
  preFooter?: ReactNode;
  /** Client card and other host chrome that scrolls with the form fields. */
  leadingContent?: ReactNode;
  submitLabel?: string;
}

export default function LaunchForm({
  binding,
  values,
  setValues,
  onSubmit,
  submitting = false,
  submitDisabledReason = null,
  prefillLoading = false,
  fieldConfidence,
  fieldLoading,
  onReanalyse,
  onOpenEmail,
  runLevelControls,
  preFooter,
  leadingContent,
  submitLabel = "Approve & Run",
}: LaunchFormProps) {
  const actionsKey = binding.actionsKey;
  const [openEmailError, setOpenEmailError] = useState<string | null>(null);

  /**
   * True from the press of RE-ANALYSE until its action drop has been queued.
   *
   * STATE, not a ref, and it gates normalisation for a reason that cost a
   * failing test to find. A fresh brief arrives FLAT at run level while the
   * stale action is still there, and the fold rule's second half deletes
   * declared ACTION keys stranded at run level — so a normalisation pass
   * between the merge and the drop wipes the brief off run level (the stale
   * action's fields are non-empty, so nothing is folded first), and the
   * rebuilt action comes back EMPTY. Suppressing normalisation for the
   * duration makes the merge-then-drop pair atomic from the rule's point of
   * view. It is state so that clearing it re-runs the effect even on the
   * failure path, where no values change would otherwise trigger it.
   */
  const [reanalysing, setReanalysing] = useState(false);

  // ── normalisation ───────────────────────────────────────────────────────
  useEffect(() => {
    if (reanalysing) return;
    if (prefillLoading) {
      // Wait only when there is NOTHING to show. Cached analyze_email values
      // should paint immediately; an empty first-paint create would freeze an
      // empty action the later shallow merge cannot enter (R13).
      const { actions } = splitLaunchValues(binding, values);
      if (actions.length === 0) {
        const hasSeed = [...binding.declaredKeys.action].some(
          (k) => !isBlank(values[k]),
        );
        if (!hasSeed) return;
      }
    }
    const next = normaliseLaunchValues(binding, values);
    if (next) setValues(next);
  }, [binding, values, setValues, prefillLoading, reanalysing]);

  const { runLevelValues, actions } = useMemo(
    () => splitLaunchValues(binding, values),
    [binding, values],
  );

  const policyOptions = useMemo(
    () => readPolicyOptions(values),
    [values],
  );

  // ── writes ──────────────────────────────────────────────────────────────
  /**
   * Route a MIXED patch to the slice that DECLARES each key. `target_interest`
   * lands on the action; `old_mortgagee_name`, `policy_type` / `carrier_name` /
   * `carrier_id` and the freeform `policy_term_key` land at run level. No
   * control has to know where a key lives, and spreading a mixed patch onto one
   * level — which is silently wrong — becomes impossible.
   */
  const writePatch = useCallback(
    (index: number, patch: Record<string, unknown>) => {
      if (!actionsKey) return;
      const split = splitLaunchValues(binding, values);
      const nextRun: Record<string, unknown> = { ...split.runLevelValues };
      const nextActions = split.actions.map((a) => ({ ...a }));
      while (nextActions.length <= index) nextActions.push({});
      const target = nextActions[index];
      for (const [key, value] of Object.entries(patch)) {
        if (binding.declaredKeys.action.has(key)) target[key] = value;
        else nextRun[key] = value;
      }
      setValues({ ...nextRun, [actionsKey]: nextActions });
    },
    [actionsKey, binding, setValues, values],
  );

  /** One run-level key, written without disturbing the action slice. */
  const writeRunLevel = useCallback(
    (key: string, value: unknown) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [setValues],
  );

  /**
   * The host's re-analysis, plus the ONE thing this form owns on top of it.
   *
   * On SUCCESS the action list is dropped so the create-rule (above) rebuilds
   * one action from the merged fresh brief. Without the drop the create-rule
   * will not rebuild an action that already exists and the drawer's shallow
   * merge cannot write INTO one, so the fresh brief would land at run level
   * where no control reads it: the AM would see a "successful" re-analysis that
   * changed nothing — the silent-success class this control exists to remove.
   *
   * On FAILURE nothing is dropped and `values` is untouched. There is no
   * snapshot/restore, because a restore has a window in which the form is empty
   * and an emptied form reads as "re-analysed, found nothing".
   *
   * The drop is a FUNCTIONAL update: by the time this promise settles the host
   * has already merged the fresh brief into `values`, and a drop computed from
   * this closure's `values` would throw that merge away.
   */
  const handleReanalyse = useCallback(async (): Promise<
    ReanalyseResult | void
  > => {
    if (!onReanalyse) return;
    setReanalysing(true);
    try {
      const result = await onReanalyse();
      if (result && result.ok === false) return result;
      if (actionsKey) {
        setValues((prev) => {
          if (!(actionsKey in prev)) return prev;
          const next = { ...prev };
          delete next[actionsKey];
          return next;
        });
      }
      return result;
    } finally {
      // Queued AFTER the drop, so the render that clears the gate is the same
      // one that has no action list — the create-rule then rebuilds from the
      // merged brief in a single pass. On the failure path this is the only
      // thing that re-runs the effect at all.
      setReanalysing(false);
    }
  }, [actionsKey, onReanalyse, setValues]);

  const onChangeAction = useCallback(
    (index: number, next: Record<string, unknown>) => {
      if (!actionsKey) return;
      const split = splitLaunchValues(binding, values);
      const nextActions = split.actions.map((a, i) => (i === index ? next : a));
      while (nextActions.length <= index) nextActions.push({});
      nextActions[index] = next;
      setValues({ ...split.runLevelValues, [actionsKey]: nextActions });
    },
    [actionsKey, binding, setValues, values],
  );

  // ── validation ──────────────────────────────────────────────────────────
  const placement = useMemo(
    () => resolveTermIdentityPlacement(binding),
    [binding],
  );

  const { validation, missingSources, blockingReason } = useMemo(
    () =>
      computeLaunchBlocker({
        binding,
        values,
        policyOptions,
        reanalysing,
        extraReason: submitDisabledReason,
      }),
    [binding, values, policyOptions, reanalysing, submitDisabledReason],
  );

  // ── projections ─────────────────────────────────────────────────────────
  const actionConfidence = projectFieldMap(
    fieldConfidence,
    binding.declaredKeys.action,
    [...binding.declaredKeys.interestRecord, ...ADDRESS_PART_KEYS],
  );
  const actionLoading = projectFieldMap(
    fieldLoading,
    binding.declaredKeys.action,
    [...binding.declaredKeys.interestRecord, ...ADDRESS_PART_KEYS],
  );

  // ── run-level control state ─────────────────────────────────────────────
  // Read through the SAME rules the payload uses, so what the AM sees and what
  // the payload carries cannot disagree: reply draft defaults ON (an untouched
  // form must behave exactly as today), the override defaults UNSET (an
  // accidental default would silently opt every run out of waiting).
  const replyDraftKey = binding.roles.replyDraft;
  const replyDraftChecked = replyDraftValue(
    replyDraftKey ? runLevelValues[replyDraftKey] : undefined,
  );

  const errorLines: string[] = [];
  if (binding.missingRoles.length > 0) {
    errorLines.push(
      `The launch contract does not declare a field this form needs: ${binding.missingRoles.join(", ")}.`,
    );
  }
  if (binding.unboundRunLevelKeys.length > 0) {
    errorLines.push(
      `The launch contract declares fields this form has no control for: ${binding.unboundRunLevelKeys.join(", ")}. They cannot be set here.`,
    );
  }
  if (missingSources.length > 0) {
    errorLines.push(
      `The selected policy term supplies no value for: ${missingSources.join(", ")}.`,
    );
  }

  return (
    // NOT a <form>: each ActionCard's JsonSchemaForm renders one.
    // Pane layout: fields scroll, Approve & Run stays pinned.
    <div
      className="flex min-h-0 flex-1 flex-col"
      data-testid="launch-form"
    >
      {/* Story 28633: tight gaps so the whole form fits a ~440×720 pane. */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
        {errorLines.length > 0 ? (
          <div
            role="alert"
            className={`rounded-lg border border-primary-error-600 px-3 py-2 ${TYPE_BODY} text-primary-error-600`}
            data-testid="launch-form-errors"
          >
            {errorLines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        ) : null}

        {/* The host's client box and the launch page's title come first — the
            page reads client, title, then the form (story 28633). */}
        {leadingContent}

        {prefillLoading ? (
          <div
            role="status"
            className={`flex items-center gap-2 rounded-md border border-primary-gray-light-200 bg-primary-gray-light-50 px-3 py-1 ${TYPE_BODY} text-primary-gray-light-700`}
            data-testid="launch-prefill-loading"
          >
            <span
              className="inline-block h-3 w-3 flex-shrink-0 animate-spin rounded-full border border-primary-gray-light-300 border-t-primary-gray-light-700"
              aria-hidden="true"
            />
            <span className="min-w-0">
              Filling in policy and form details from this email…
            </span>
          </div>
        ) : null}

        {onReanalyse || onOpenEmail ? (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-start gap-3">
              {onReanalyse ? (
                <ReanalyseControl
                  onReanalyse={handleReanalyse}
                  disabled={submitting}
                />
              ) : null}
              {onOpenEmail ? (
                <OpenEmailLink
                  testId="launch-open-email"
                  // Level with the Re-analyse chip's label, not its border.
                  className="mt-[3px]"
                  onClick={() => {
                    const result = onOpenEmail();
                    setOpenEmailError(
                      result && result.ok === false ? result.message : null,
                    );
                  }}
                />
              ) : null}
            </div>
            {onOpenEmail && openEmailError ? (
              <p
                role="alert"
                className={`${TYPE_BODY} text-primary-warning-700`}
                data-testid="launch-open-email-error"
              >
                {openEmailError}
              </p>
            ) : null}
          </div>
        ) : null}

        <PolicyActionGroup
          binding={binding}
          runLevelValues={runLevelValues}
          actions={actions}
          placement={placement}
          policyOptions={policyOptions}
          writePatch={writePatch}
          onChangeAction={onChangeAction}
          submitting={submitting}
          fieldConfidence={actionConfidence}
          fieldLoading={actionLoading}
          actionErrors={validation.actionErrors}
          termFieldLabel={titleOf(
            placement.level === "action"
              ? binding.actionItemSchema
              : binding.runLevelSchema,
            placement.keys[0] ?? "policy_number",
            "Policy Number",
          )}
          effectiveDateLabel={titleOf(
            binding.actionItemSchema,
            binding.roles.effectiveDate ?? "effective_date",
            "Effective Date",
          )}
          oldInterestLabel={titleOf(
            binding.runLevelSchema,
            OLD_INTEREST_NAME_KEY,
            "Existing mortgagee",
          )}
        />

        {/* "Add another policy" is hidden until multi-policy launches work
            (story 28633). AddPolicyAffordance stays for that day. */}

        {/* RUN OPTIONS — outside every collapsible (AC-5). Skip-the-wait lives
            on the running drawer, not here. */}
        <div className="flex flex-col gap-1" data-testid="launch-run-level">
          <div className={`${TYPE_LABEL} text-primary-gray-light-600`}>
            Run options
          </div>
          <div className="flex flex-col divide-y divide-primary-gray-light-200 rounded-lg border border-primary-gray-light-200">
            {replyDraftKey ? (
              <div className="px-3 py-1">
                <ReplyDraftControl
                  fieldKey={replyDraftKey}
                  label="Draft the reply email"
                  checked={replyDraftChecked}
                  disabled={submitting}
                  onChange={(next) => writeRunLevel(replyDraftKey, next)}
                />
              </div>
            ) : null}
            {runLevelControls ? (
              <div className="px-3 py-1.5">{runLevelControls}</div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 flex-col gap-1 border-t border-primary-gray-light-200 bg-base-white pt-2">
        {preFooter ? (
          <div className="mb-1" data-testid="launch-pre-footer">
            {preFooter}
          </div>
        ) : null}
        {/* Why it is greyed out, in ora's tooltip, not a native title (story
            28633). The span is the hover target: a disabled button fires no
            pointer events. Node title + describeChild: no aria-label or
            native title lands on it; a null title never opens. Always
            wrapped, so the button is not remounted when it enables. */}
        <CustomTooltip
          id="launch-submit"
          title={blockingReason ? <span>{blockingReason}</span> : null}
          describeChild
          placement="top"
          arrow
          isOnModal
        >
          <span className="flex w-full">
            <button
              type="button"
              disabled={submitting || !!blockingReason}
              onClick={() => onSubmit(values)}
              // The pane's ONE primary-action look (Review & Approve's).
              className={`${PRIMARY_ACTION_BUTTON_CLASS} touch-target disabled:opacity-50`}
              data-testid="launch-submit"
            >
              {submitting ? "Starting…" : submitLabel}
            </button>
          </span>
        </CustomTooltip>
        {/* Not while re-analysing: the reason is on the tooltip and the
            Re-analyse control already says it is working (story 28873). */}
        {blockingReason && !reanalysing ? (
          <p
            className={`${TYPE_BODY} text-primary-gray-light-700`}
            data-testid="launch-submit-blocked-reason"
          >
            {blockingReason}
          </p>
        ) : null}
      </div>
    </div>
  );
}
